/* eslint-disable react/jsx-props-no-spreading */
import { faCalendarRange, faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ClickAwayListener,
  FormControlLabel,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Stack,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { DateFiltering, LookbackDatePart, ReportTemplate } from '../../validationSchemas/ReportTemplateSchema';

// The user-facing modes in the popover. We keep these separate from the wire
// shape so the UI can stay readable; serialization back to {enabled, dateRange,
// lookback} happens in `applySelection` below.
type Mode = 'disabled' | 'custom' | 'lookback_day' | 'lookback_week' | 'lookback_month' | 'mtd';

const MODE_OPTIONS: { value: Mode; label: string }[] = [
  { value: 'disabled', label: 'Disabled (no date filter)' },
  { value: 'custom', label: 'Custom range' },
  { value: 'lookback_day', label: 'Last X Days' },
  { value: 'lookback_week', label: 'Last X Weeks' },
  { value: 'lookback_month', label: 'Last X Months' },
  { value: 'mtd', label: 'Month to date' },
];

const LOOKBACK_TO_MODE: Record<LookbackDatePart, Mode> = {
  Day: 'lookback_day',
  Week: 'lookback_week',
  Month: 'lookback_month',
  month_to_date: 'mtd',
};

const MODE_TO_DATE_PART: Partial<Record<Mode, LookbackDatePart>> = {
  lookback_day: 'Day',
  lookback_week: 'Week',
  lookback_month: 'Month',
  mtd: 'month_to_date',
};

// Pull a Mode out of the stored form value so the popover opens to the right
// option when reopened.
const deriveMode = (dateFiltering: DateFiltering | undefined): Mode => {
  if (!dateFiltering || !dateFiltering.enabled) return 'disabled';
  if (dateFiltering.dateRange?.[0] && dateFiltering.dateRange?.[1]) return 'custom';
  if (dateFiltering.lookback) return LOOKBACK_TO_MODE[dateFiltering.lookback.datePart];
  return 'disabled';
};

// Short text shown on the pill button summarizing the current selection.
const summarize = (dateFiltering: DateFiltering | undefined): string => {
  if (!dateFiltering || !dateFiltering.enabled) return 'Date Filtering';
  const [start, end] = dateFiltering.dateRange ?? [];
  if (start && end) {
    return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
  }
  if (dateFiltering.lookback) {
    const { datePart, length } = dateFiltering.lookback;
    if (datePart === 'month_to_date') return 'Month to date';
    return `Last ${length} ${datePart}${length === 1 ? '' : 's'}`;
  }
  return 'Date Filtering';
};

function DateFilteringControl() {
  const { watch, setValue } = useFormContext<ReportTemplate>();
  const dateFiltering = watch('dateFiltering');

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  // Local draft state — the popover is a transactional editor that only
  // commits on Apply. This mirrors the "Cancel / Apply" pattern in the mock.
  const [mode, setMode] = useState<Mode>(deriveMode(dateFiltering));
  const [start, setStart] = useState<Date | null>(dateFiltering?.dateRange?.[0] ?? null);
  const [end, setEnd] = useState<Date | null>(dateFiltering?.dateRange?.[1] ?? null);
  const [length, setLength] = useState<number>(dateFiltering?.lookback?.length ?? 30);

  // When the popover opens, sync the draft from form state so we always start
  // from the truth and don't leak previous edits.
  useEffect(() => {
    if (!open) return;
    setMode(deriveMode(dateFiltering));
    setStart(dateFiltering?.dateRange?.[0] ?? null);
    setEnd(dateFiltering?.dateRange?.[1] ?? null);
    setLength(dateFiltering?.lookback?.length ?? 30);
    // We intentionally only re-sync on open — the popover is a snapshot editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const togglePopper = () => setOpen((prev) => !prev);
  const closePopper = () => setOpen(false);

  const isApplyEnabled = useMemo(() => {
    if (mode === 'disabled') return true;
    if (mode === 'mtd') return true;
    if (mode === 'custom') return !!start && !!end && start <= end;
    return Number.isFinite(length) && length > 0; // lookback day/week/month
  }, [mode, start, end, length]);

  const applySelection = () => {
    let next: DateFiltering;
    switch (mode) {
      case 'custom':
        next = { enabled: true, dateRange: [start, end] };
        break;
      case 'mtd':
        next = { enabled: true, lookback: { datePart: 'month_to_date', length: 0 } };
        break;
      case 'lookback_day':
      case 'lookback_week':
      case 'lookback_month':
        next = {
          enabled: true,
          lookback: { datePart: MODE_TO_DATE_PART[mode]!, length },
        };
        break;
      case 'disabled':
      default:
        next = { enabled: false };
    }
    setValue('dateFiltering', next, { shouldDirty: true, shouldValidate: true });
    closePopper();
  };

  const isLookbackMode = mode === 'lookback_day' || mode === 'lookback_week' || mode === 'lookback_month';

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={togglePopper}
        variant="outlined"
        color="info"
        aria-label="Date Filtering"
        startIcon={<FontAwesomeIcon icon={faCalendarRange} color="#2D2D2D" style={{ fontSize: '16px' }} />}
        endIcon={<FontAwesomeIcon icon={faCaretDown} color="#2D2D2D" style={{ fontSize: '12px' }} />}
      >
        &nbsp;{summarize(dateFiltering)}&nbsp;
      </Button>
      <Popper
        anchorEl={anchorRef.current}
        open={open}
        placement="bottom-start"
        onClick={(e) => e.stopPropagation()}
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={closePopper}>
          <Paper sx={{ p: 2, width: 360 }}>
            <Stack spacing={2}>
              <RadioGroup
                value={mode}
                onChange={(e) => setMode(e.target.value as Mode)}
                aria-label="Date filtering mode"
              >
                {MODE_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio size="small" />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>

              {mode === 'custom' && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <DesktopDatePicker
                    label="Start"
                    value={start}
                    onChange={(value) => setStart(value)}
                    maxDate={end ?? undefined}
                    slotProps={{ textField: { size: 'small', variant: 'outlined' } }}
                  />
                  <Typography>→</Typography>
                  <DesktopDatePicker
                    label="End"
                    value={end}
                    onChange={(value) => setEnd(value)}
                    minDate={start ?? undefined}
                    slotProps={{ textField: { size: 'small', variant: 'outlined' } }}
                  />
                </Stack>
              )}

              {isLookbackMode && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography>Last</Typography>
                  <MuiTextField
                    type="number"
                    size="small"
                    value={length}
                    onChange={(e) => setLength(Number(e.target.value))}
                    inputProps={{ min: 1, 'aria-label': 'Lookback length' }}
                    sx={{ width: 80 }}
                  />
                  <Typography>
                    {mode === 'lookback_day' && (length === 1 ? 'day' : 'days')}
                    {mode === 'lookback_week' && (length === 1 ? 'week' : 'weeks')}
                    {mode === 'lookback_month' && (length === 1 ? 'month' : 'months')}
                  </Typography>
                </Stack>
              )}

              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button onClick={closePopper} variant="outlined" size="small">
                  Cancel
                </Button>
                <Button
                  onClick={applySelection}
                  disabled={!isApplyEnabled}
                  variant="contained"
                  size="small"
                  aria-label={`${isApplyEnabled ? 'Enabled' : 'Disabled'} Apply Date Filter`}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

export default DateFilteringControl;
